/**
 * How a clinical limit is named, measured and read on screen.
 *
 * ── Why this is a module and not a block inside a component ─────────────────
 * Two screens now show the same limits. Settings → Clinique shows all of them,
 * adjustable; the first-run setup screen shows the headline ones, read-only,
 * while the owner decides whether to accept the protocol they came from. Two
 * screens showing "20 mg/day" under two different labels — or one of them
 * rounding, or dropping a unit — is the kind of divergence a pharmacist has no
 * way to notice and every reason to trust.
 *
 * ── What is deliberately NOT here ───────────────────────────────────────────
 * No numbers. Which fields exist, their type, their unit and what the protocol
 * says each one is all come from the server (`overridable` on the profile, or
 * the protocol object in the catalogue). This module only decides how to render
 * what it is handed, so a screen can never present a field the server would
 * reject or a value it did not send.
 */

/**
 * Server field key -> i18n key suffix, under `userAccount.clinical.adjust.fields`.
 *
 * Two reasons this map exists rather than interpolating the field name. The
 * nested keys contain a dot, which vue-i18n reads as a path separator, so
 * `suspiciousPatterns.zeroDoses` cannot be a translation key. And a limit needs
 * a clinician's name, not a variable name: `warnDailyDose` means nothing on a
 * screen.
 */
export const FIELD_LABEL_KEYS = {
  maxDailyDose: 'maxDaily',
  maxSingleDose: 'maxSingle',
  maxWeeklyDose: 'maxWeekly',
  minDose: 'minDose',
  minPracticalDose: 'minPractical',
  warnDailyDose: 'warnDaily',
  warnWeeklyDose: 'warnWeekly',
  maxWeeklyChangePercent: 'maxChange',
  criticalInrHigh: 'inrHigh',
  criticalInrVeryHigh: 'inrVeryHigh',
  criticalInrLow: 'inrLow',
  criticalInrVeryLow: 'inrVeryLow',
  doseGranularityMg: 'granularity',
  'suspiciousPatterns.allSameDose': 'patternAllSame',
  'suspiciousPatterns.zeroDoses': 'patternZero',
  'suspiciousPatterns.maxDoseDays': 'patternMaxDays'
}

/**
 * The limits shown before "show all", and the whole of what the first-run
 * screen shows.
 *
 * Not a severity ranking: it is the set a pharmacist would name if asked what
 * the tool enforces. Everything else is a threshold sitting behind one of
 * these, and stays one click away rather than turning the ordinary case into a
 * form of sixteen numbers.
 */
export const HEADLINE_FIELDS = Object.freeze([
  'maxDailyDose',
  'maxWeeklyDose',
  'minPracticalDose',
  'doseGranularityMg',
  'maxWeeklyChangePercent',
  'criticalInrHigh'
])

/**
 * Falls back to the raw key: an unlabelled field is still better than a blank
 * row, and it tells whoever sees it exactly which key is missing.
 */
export function fieldLabel(t, field) {
  const key = FIELD_LABEL_KEYS[field]
  return key ? t(`userAccount.clinical.adjust.fields.${key}`) : field
}

/**
 * The unit to render beside a value, or null for none.
 *
 * An INR threshold takes no suffix: its label already names the test, and the
 * unit the server sends is that name in English, which has no place on a French
 * screen. 'days' is a server-side word for the same reason, translated here.
 */
export function unitFor(t, row) {
  if (row.type === 'inr' || row.type === 'boolean') return null
  if (row.unit === 'days') return t('userAccount.clinical.adjust.units.days')
  return row.unit
}

/** A limit as it is read: the number and its unit, or On/Off for a flag. */
export function displayValue(t, value, unit) {
  if (typeof value === 'boolean') {
    return value ? t('userAccount.clinical.adjust.on') : t('userAccount.clinical.adjust.off')
  }
  return unit ? `${value} ${unit}` : String(value)
}

/**
 * Where a limit lives on a serialised protocol.
 *
 * The catalogue sends every protocol whole, so what a protocol publishes for a
 * field can be read off the object rather than waited for from a second
 * request. What the client still does not decide is the LIST — which fields
 * exist at all, and their type, unit and hard-cap flag.
 */
export function publishedValue(protocol, field) {
  if (!protocol) return undefined
  if (field === 'doseGranularityMg') return protocol.doseGranularityMg
  const safety = protocol.safety ?? {}
  const [group, leaf] = field.split('.')
  return leaf ? safety[group]?.[leaf] : safety[group]
}

/**
 * The headline limits a protocol publishes, in the shape a row expects.
 *
 * Read straight off the protocol, so it works for one that has been SELECTED
 * but not saved — which is the entire state the first-run screen lives in, and
 * the state Settings → Clinique falls back to when the server offers no
 * adjustable field list.
 *
 * @param {Object|null} protocol  A protocol from the catalogue.
 * @param {string} doseUnit       The molecule's dose unit, e.g. 'mg'.
 */
export function headlineRows(protocol, doseUnit = 'mg') {
  if (!protocol) return []
  const safety = protocol.safety ?? {}
  return [
    { field: 'maxDailyDose', type: 'mg', unit: doseUnit, value: safety.maxDailyDose },
    { field: 'maxWeeklyDose', type: 'mg', unit: doseUnit, value: safety.maxWeeklyDose },
    { field: 'minPracticalDose', type: 'mg', unit: doseUnit, value: safety.minPracticalDose },
    { field: 'doseGranularityMg', type: 'grid', unit: doseUnit, value: protocol.doseGranularityMg },
    {
      field: 'maxWeeklyChangePercent',
      type: 'percent',
      unit: '%',
      value: safety.maxWeeklyChangePercent
    },
    { field: 'criticalInrHigh', type: 'inr', unit: null, value: safety.criticalInrHigh }
  ].map((row) => ({ ...row, base: row.value, active: false, differs: false, reason: '', headline: true }))
}
